import styled from 'styled-components';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import type { BackgroundType } from '../../../types';
import { getClassName, getPressureUnit, getSpeedUnit } from '../../helpers';
import type { ForecastListItem, RequestResponse } from '../api/request';

export const ForecastDetail = ({
  data
}: {
  data: RequestResponse<ForecastListItem>;
}) => {
  const { backgroundColor, backgroundType, language } = useSettings();
  const { t } = useLingui();

  const {
    main: { pressure, humidity },
    wind: { speed: wind }
  } = data.list[0];
  const { language: locale } = language ?? {};

  const windUnits = getSpeedUnit(locale);
  const pressureUnits = getPressureUnit(locale);

  const pressureValue = (() => {
    if (pressureUnits === 'hPa') {
      return `${pressure.toFixed(0)} ${t`hPa`}`;
    }

    if (pressureUnits === 'inHg') {
      return `${(pressure / 33.864).toFixed(1)} ${t`inHg`}`;
    }

    return `${(pressure / 1.33322).toFixed(0)} ${t`mmHg`}`;
  })();

  const windValue = (() => {
    if (windUnits === 'mph') {
      return `${wind.toFixed(1)} ${t`mph`}`;
    }

    if (windUnits === 'm/s') {
      return `${(wind / 2.237).toFixed(1)} ${t`m/s`}`;
    }

    return `${(wind * 1.609).toFixed(1)} ${t`km/h`}`;
  })();

  return (
    <Component>
      <Item title={t`Wind`}>
        <Icon
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="12"
          viewBox="0 0 14 12"
        >
          <path d="M5.919 1.53a.615.615 0 0 1 .77-.108.68.68 0 0 1 .296.752.642.642 0 0 1-.617.493H.636c-.351 0-.636.298-.636.666C0 3.702.285 4 .636 4h5.729c.863.004 1.623-.603 1.849-1.479.225-.875-.14-1.8-.89-2.254A1.845 1.845 0 0 0 5.016.59a.69.69 0 0 0 .003.943c.249.26.652.258.9-.003zm1.006 9.88c.61.643 1.558.776 2.308.323.75-.453 1.116-1.379.89-2.254C9.897 8.603 9.138 7.996 8.272 8H.636C.285 8 0 8.298 0 8.667c0 .368.285.666.636.666h7.638a.643.643 0 0 1 .62.493.68.68 0 0 1-.296.752.615.615 0 0 1-.77-.108.616.616 0 0 0-.9-.003.69.69 0 0 0-.003.943zm4.173-7.785a.923.923 0 0 1 1.152-.158c.374.227.556.688.444 1.124a.963.963 0 0 1-.92.742H.636C.285 5.333 0 5.632 0 6c0 .368.285.667.636.667h11.139c1.009-.002 1.89-.712 2.15-1.731.26-1.02-.166-2.095-1.039-2.623a2.153 2.153 0 0 0-2.687.368.69.69 0 0 0-.001.943c.248.26.651.261.9.001z" />
        </Icon>

        <Value
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
        >
          {windValue}
        </Value>
      </Item>

      <Item title={t`Pressure`}>
        <Icon
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
        >
          <path d="M9.193 5.707a2.545 2.545 0 1 1-.9-.9l1.252-1.252a.636.636 0 1 1 .9.9L9.193 5.707zm-2.83 6.985v-.601a.636.636 0 0 1 1.273 0v.601a5.73 5.73 0 0 0 5.056-5.056h-.601a.636.636 0 0 1 0-1.272h.601a5.73 5.73 0 0 0-5.056-5.056v.601a.636.636 0 0 1-1.272 0v-.601a5.73 5.73 0 0 0-5.056 5.056h.601a.636.636 0 0 1 0 1.272h-.601a5.73 5.73 0 0 0 5.056 5.056zM7 14A7 7 0 1 1 7 0a7 7 0 0 1 0 14zm0-5.727a1.273 1.273 0 1 0 0-2.546 1.273 1.273 0 0 0 0 2.546z" />
        </Icon>

        <Value
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
        >
          {pressureValue}
        </Value>
      </Item>

      <Item title={t`Humidity`}>
        <Icon
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="14"
          viewBox="0 0 12 14"
        >
          <path d="M2.701 4.838a4.547 4.547 0 0 0-1.013 5.01c.723 1.718 2.424 2.839 4.312 2.839 1.888 0 3.59-1.12 4.312-2.839a4.547 4.547 0 0 0-1.013-5.01L5.997 1.586 2.701 4.838zM6.468.192l3.773 3.717a5.846 5.846 0 0 1 1.302 6.442C10.615 12.56 8.427 14 6 14S1.385 12.56.457 10.35a5.846 5.846 0 0 1 1.301-6.44L5.525.193a.674.674 0 0 1 .943 0z" />
        </Icon>

        <Value
          _backgroundType={backgroundType}
          _backgroundColor={backgroundColor}
        >
          {humidity.toFixed(0)} %
        </Value>
      </Item>
    </Component>
  );
};

const Component = styled.div.attrs({
  className: getClassName('forecast-detail', 'component')
})`
  display: flex;
  flex-direction: column;
`;

const Item = styled.div.attrs({
  className: getClassName('forecast-detail', 'item')
})`
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.39;
  color: #ffffff;

  &:not(:last-child) {
    margin-bottom: 13px;
  }
`;

const Value = styled.div.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
}>({
  className: getClassName('forecast-detail', 'value')
})`
  white-space: nowrap;
  margin-left: 6px;

  ${({ _backgroundType, _backgroundColor }) =>
    _backgroundType === 'color' && _backgroundColor
      ? `color: ${getBrightInvertedColor(_backgroundColor, ['rgb(255, 255, 255)', 'rgb(29, 33, 41)'])};`
      : ''}
`;

const Icon = styled.svg.attrs<{
  _backgroundType: BackgroundType;
  _backgroundColor: string;
}>({
  className: getClassName('forecast-detail', 'icon')
})`
  width: 14px;
  height: 14px;

  fill: ${({ _backgroundType, _backgroundColor }) =>
    _backgroundType === 'color' && _backgroundColor
      ? `${getBrightInvertedColor(_backgroundColor, ['rgb(255, 255, 255)', 'rgb(29, 33, 41)'])}`
      : '#fff'};
`;
